import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AuthPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthPage = ({ onBack, initialMode = 'login', resetToken = null }) => {
  const [view, setView] = useState(resetToken ? 'reset' : (initialMode === 'login' ? 'login' : 'register'));
  const [username, setUsername] = useState('');
  const [identifier, setIdentifier] = useState(''); // For login: username or email
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const { login, register, error, clearError } = useAuth();

  // Verify reset token on mount if present
  useEffect(() => {
    const verifyToken = async () => {
      if (resetToken) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify-reset-token?token=${resetToken}`);
          const data = await response.json();
          setTokenValid(data.valid);
          if (!data.valid) {
            setLocalError(data.message || 'Invalid or expired reset link');
          }
        } catch {
          setTokenValid(false);
          setLocalError('Unable to verify reset link');
        }
      }
    };
    verifyToken();
  }, [resetToken]);

  const isLogin = view === 'login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    // Validation for login
    if (isLogin) {
      if (!identifier || !password) {
        setLocalError('Please fill in all fields');
        return;
      }
    } else {
      // Validation for registration
      if (!username || !email || !password) {
        setLocalError('Please fill in all fields');
        return;
      }
      if (username.length < 3) {
        setLocalError('Username must be at least 3 characters');
        return;
      }
      if (username.length > 30) {
        setLocalError('Username must be 30 characters or less');
        return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        setLocalError('Username can only contain letters, numbers, and underscores');
        return;
      }
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setLocalError('Password must be at least 6 characters');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (isLogin) {
        await login(identifier, password);
      } else {
        await register(username, email, password);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = () => {
    setView(isLogin ? 'register' : 'login');
    setLocalError('');
    setSuccessMessage('');
    clearError();
    setUsername('');
    setIdentifier('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const showForgotPassword = () => {
    setView('forgot');
    setLocalError('');
    setSuccessMessage('');
    clearError();
    setEmail('');
  };

  const backToLogin = () => {
    setView('login');
    setLocalError('');
    setSuccessMessage('');
    clearError();
    setPassword('');
    setConfirmPassword('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccessMessage('');

    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('If an account exists with this email, you will receive a password reset link shortly.');
        setEmail('');
      } else {
        setLocalError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setLocalError('Unable to send reset link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccessMessage('');

    if (!password) {
      setLocalError('Please enter a new password');
      return;
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Password reset successful! You can now sign in with your new password.');
        setPassword('');
        setConfirmPassword('');
        // Switch to login view after a short delay
        setTimeout(() => {
          setView('login');
          setSuccessMessage('');
        }, 3000);
      } else {
        setLocalError(data.detail || 'Failed to reset password. Please try again.');
      }
    } catch {
      setLocalError('Unable to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {onBack && (
          <button onClick={onBack} className="auth-close-btn" disabled={isSubmitting}>
            &times;
          </button>
        )}
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-logo">AIthlete</h1>
            <p className="auth-tagline">Train Smarter</p>
          </div>

          {/* Forgot Password View */}
          {view === 'forgot' && (
            <>
              <h2 className="auth-title">Reset Password</h2>
              <p className="auth-subtitle">Enter your email address and we'll send you a link to reset your password.</p>

              <form onSubmit={handleForgotPassword} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>

                {localError && (
                  <div className="auth-error">{localError}</div>
                )}

                {successMessage && (
                  <div className="auth-success">{successMessage}</div>
                )}

                <button
                  type="submit"
                  className="auth-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-switch">
                <p>
                  Remember your password?
                  <button
                    type="button"
                    onClick={backToLogin}
                    className="auth-switch-btn"
                    disabled={isSubmitting}
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </>
          )}

          {/* Reset Password View (with token) */}
          {view === 'reset' && (
            <>
              <h2 className="auth-title">Set New Password</h2>

              {tokenValid === false ? (
                <>
                  <div className="auth-error">{localError || 'This reset link is invalid or has expired.'}</div>
                  <div className="auth-switch">
                    <p>
                      Need a new link?
                      <button
                        type="button"
                        onClick={showForgotPassword}
                        className="auth-switch-btn"
                      >
                        Request Reset
                      </button>
                    </p>
                  </div>
                </>
              ) : tokenValid === null ? (
                <p className="auth-subtitle">Verifying reset link...</p>
              ) : (
                <>
                  <p className="auth-subtitle">Enter your new password below.</p>

                  <form onSubmit={handleResetPassword} className="auth-form">
                    <div className="form-group">
                      <label htmlFor="password">New Password</label>
                      <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>

                    {localError && (
                      <div className="auth-error">{localError}</div>
                    )}

                    {successMessage && (
                      <div className="auth-success">{successMessage}</div>
                    )}

                    <button
                      type="submit"
                      className="auth-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* Login View */}
          {view === 'login' && (
            <>
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <input
                    type="text"
                    id="identifier"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Email"
                    disabled={isSubmitting}
                    autoComplete="username"
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="button"
                  onClick={showForgotPassword}
                  className="forgot-password-btn"
                  disabled={isSubmitting}
                >
                  Forgot password?
                </button>

                {(localError || error) && (
                  <div className="auth-error">{localError || error}</div>
                )}

                {successMessage && (
                  <div className="auth-success">{successMessage}</div>
                )}

                <button
                  type="submit"
                  className="auth-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div className="auth-switch">
                <p>
                  Don't have an account?
                  <button
                    type="button"
                    onClick={switchMode}
                    className="auth-switch-btn"
                    disabled={isSubmitting}
                  >
                    Sign Up
                  </button>
                </p>
              </div>
            </>
          )}

          {/* Register View */}
          {view === 'register' && (
            <>
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    disabled={isSubmitting}
                    autoComplete="username"
                  />
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>

                {(localError || error) && (
                  <div className="auth-error">{localError || error}</div>
                )}

                <button
                  type="submit"
                  className="auth-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating account...' : 'Create Account'}
                </button>

                <p className="auth-age-disclaimer">
                  You must be at least 13 years old to create an account.
                </p>
              </form>

              <div className="auth-switch">
                <p>
                  Already have an account?
                  <button
                    type="button"
                    onClick={switchMode}
                    className="auth-switch-btn"
                    disabled={isSubmitting}
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
